// timeService.js
// Time service with fallback for daily challenge date resolution

const isDevelopment = process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

/**
 * Get the current date in YYYY-MM-DD format
 * In development: uses client time directly
 * In production: tries Netlify function, then WorldTimeAPI, then client time
 * @returns {Promise<string>} Date string in YYYY-MM-DD format
 */
export async function getCurrentDate() {
    if (isDevelopment) {
        return getClientDate();
    }

    // Production: try Netlify function first
    try {
        const response = await fetch('/api/get_time', {
            method: 'GET',
            cache: 'no-store',
        });

        if (response.ok) {
            const data = await response.json();
            if (data.date && data.date !== 'prerender') {
                return data.date;
            }
        }
    } catch (error) {
        console.warn('Netlify time service failed, trying fallback:', error);
    }

    // Fallback: WorldTimeAPI
    try {
        const response = await fetch('https://worldtimeapi.org/api/ip', {
            method: 'GET',
            cache: 'no-store',
        });

        if (response.ok) {
            const data = await response.json();
            // WorldTimeAPI returns datetime in ISO format
            const date = data.datetime?.split('T')[0];
            if (date) {
                return date;
            }
        }
    } catch (error) {
        console.warn('WorldTimeAPI failed, using client time:', error);
    }

    // Last resort: client time with warning
    console.warn('Using client time for daily challenge - may not match server');
    return getClientDate();
}

/**
 * Get client's current date in YYYY-MM-DD format (UTC)
 */
function getClientDate() {
    const now = new Date();
    return now.toISOString().split('T')[0];
}

/**
 * Calculate challenge number from a start date
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @param {string} startDate - Start date for challenge #1 in YYYY-MM-DD format
 * @returns {number} Challenge number (1-indexed)
 */
export function getChallengeNumber(currentDate, startDate = '2026-01-23') {
    const current = new Date(currentDate);
    const start = new Date(startDate);

    // Calculate difference in days
    const diffTime = current.getTime() - start.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Challenge #1 is on start date, so add 1
    return diffDays + 1;
}

const timeService = {
    getCurrentDate,
    getChallengeNumber,
};

export default timeService;
